var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { PureComponent } from 'react';
import { View, TextInput, Animated, Easing, StyleSheet, I18nManager, Platform, TouchableOpacity, Image, } from 'react-native';
import PropTypes from 'prop-types';
// Unit components
import LabelText from './components/LabelText';
import Line from './components/Line';
import HelperText from './components/HelperText';
import CounterText from './components/CounterText';
// Utility functions
import convertRestrictionToString from './utils/convertRestrictionToString';
import isValueRestricted from './utils/isValueRestricted';
import getContainerStyle from './utils/getContainerStyle';
import getInputColor from './utils/getInputColor';
// District UI components
import R from '../../core-res/R';
import DRNImage from '../Image/DRNImage';
// Theme Support
import { withTheme } from '../../theme';
import { withSmartKey } from '../../smartkey';
// Variants
const VARIANT_MINIMAL = 'MINIMAL';
const VARIANT_STANDARD = 'STANDARD';
class DRNInputField extends PureComponent {
    constructor(props) {
        super(props);
        this.focus = () => {
            const { disabled, editable } = this.props;
            if (!disabled && editable) {
                this.input.focus();
            }
        };
        this.blur = () => {
            this.input.blur();
        };
        this.clear = () => {
            this.input.clear();
        };
        this.handleFocus = () => {
            this.props.onFocus && this.props.onFocus();
            this.setState({ focused: true, receivedFocus: true });
        };
        this.handleBlur = () => {
            this.props.onBlur && this.props.onBlur();
            this.setState({ focused: false });
        };
        this.handleChangeText = text => {
            this.props.onChangeText && this.props.onChangeText(text);
        };
        this.handleClearText = () => {
            this.handleChangeText('');
        };
        this.renderAccessory = props => {
            const { renderAccessory } = this.props;
            if (typeof renderAccessory !== 'function') {
                return null;
            }
            return <View style={styles.renderAccessory}>{renderAccessory(props)}</View>;
        };
        const { value, error } = this.props;
        this.mounted = false;
        this.state = {
            text: value,
            focus: new Animated.Value(error ? -1 : 0.01),
            focused: false,
            receivedFocus: false,
            error,
        };
    }
    componentWillReceiveProps(props) {
        const { text, error } = this.state;
        if (props.value !== null && props.value !== text) {
            this.setState({ text: props.value });
        }
        if (props.error && props.error !== error) {
            this.setState({ error: props.error });
        }
    }
    componentDidMount() {
        this.mounted = true;
    }
    componentWillUnmount() {
        this.mounted = false;
    }
    componentWillUpdate(props, state) {
        const { error } = this.props;
        const { focus, focused } = this.state;
        if (props.error !== error || focused ^ state.focused) {
            Animated.timing(focus, {
                toValue: props.error ? -1 : state.focused ? 1 : 0.01,
                duration: R.animation.timing.instant,
                easing: Easing.inOut(Easing.ease),
            }).start(() => {
                if (this.mounted) {
                    this.setState((state, { error }) => ({ error }));
                }
            });
        }
    }
    value() {
        const { text, receivedFocus } = this.state;
        const { value, defaultValue } = this.props;
        return receivedFocus || value != null || defaultValue == null ? text : defaultValue;
    }
    isFocused() {
        return this.input.isFocused();
    }
    render() {
        const { receivedFocus, focus, focused, error, text = '' } = this.state;
        const _a = this.props, { style, defaultValue, editable, variant, textAlign, size, disabled, getValueLength, iconLeft, renderAccessory, label, helper, characterRestriction, theme, clearButtonMode } = _a, props = __rest(_a, ["style", "defaultValue", "editable", "variant", "textAlign", "size", "disabled", "getValueLength", "iconLeft", "renderAccessory", "label", "helper", "characterRestriction", "theme", "clearButtonMode"]);
        let { value } = this.props;
        const defaultVisible = !(receivedFocus || value != null || defaultValue == null);
        value = defaultVisible ? defaultValue : text;
        const active = !!value;
        const count = getValueLength(value);
        const restricted = isValueRestricted(count, characterRestriction);
        const limit = convertRestrictionToString(characterRestriction);
        // containerStyle based on variant
        const containerBorderColor = restricted
            ? theme.textAlert
            : focus.interpolate({
                inputRange: [-1, 0, 1],
                outputRange: [theme.textAlert, disabled ? theme.borderSubtle : theme.borderDivide, theme.tintPrimary],
            });
        const containerBorderWidth = restricted
            ? R.border.width.thick
            : focus.interpolate({
                inputRange: [-1, 0, 1],
                outputRange: [R.border.width.thick, R.border.width.thin, R.border.width.thick],
            });
        const containerStyle = getContainerStyle(variant, disabled, containerBorderColor, containerBorderWidth, theme);
        // TextInput Style
        const fontSize = size === 'medium' ? R.typography.uiBaseline.fontSize : R.typography.uiSmall.fontSize;
        const iconLeftSize = 16;
        const labelTextMarginLeft = iconLeft ? iconLeftSize + R.spacing.xs : 0;
        const inputStyle = Object.assign({ top: variant === VARIANT_MINIMAL ? 2 : 0, textAlign,
            fontSize, color: getInputColor(disabled, defaultVisible, theme) }, Platform.select({
            web: {
                width: '100%',
            },
            ios: {
                lineHeight: 0,
            },
        }));
        return (<View onStartShouldSetResponder={() => true} onResponderRelease={this.focus}>
        <View style={[styles.container]}>
          {disabled && <Line type="dotted" color={theme.textSecondary}/>}
          {!!label && (<View style={{ height: R.spacing.xl, zIndex: 10 }}>
              <LabelText {...{ fontSize, active, focused, restricted, disabled, variant, labelTextMarginLeft, theme }}>
                {label}
              </LabelText>
            </View>)}
          <Animated.View style={[styles.row, containerStyle]}>
            {iconLeft && (<DRNImage flipOnRTL source={iconLeft} style={[styles.iconLeft, { width: iconLeftSize, height: iconLeftSize }]} editable={!disabled && editable}/>)}
            <TextInput style={[styles.input, inputStyle, style]} selectionColor={theme.tintPrimary} {...props} editable={!disabled && editable} onChangeText={this.handleChangeText} onFocus={this.handleFocus} onBlur={this.handleBlur} value={value} ref={ref => (this.input = ref)}/>
            {!!value &&
            clearButtonMode !== 'never' && (<TouchableOpacity hitSlop={{
            top: R.spacing.xs,
            right: R.spacing.xs,
            bottom: R.spacing.xs,
            left: R.spacing.xs,
        }} activeOpacity={R.opacity.opaque()} onPress={this.handleClearText}>
                  <DRNImage flipOnRTL source={require('../../../images/ic-system-status-fail-fill.png')} style={[styles.iconClose, { tintColor: theme.darkSecondary }]}/>
                </TouchableOpacity>)}
            {clearButtonMode === 'never' && this.renderAccessory({ editable: !disabled && editable })}
          </Animated.View>
        </View>
        {(helper || error || characterRestriction) && (<Animated.View style={styles.helperContainerStyle}>
            <View style={styles.flex}>
              <HelperText helperText={helper} errorText={error} disabled={disabled} focus={focus} theme={theme}/>
            </View>

            <CounterText {...{ count, limit, restricted, disabled, theme }}/>
          </Animated.View>)}
      </View>);
    }
}
DRNInputField.smartKeyName = 'label';
DRNInputField.propTypes = Object.assign({}, TextInput.propTypes, { 
    // Custom propTypes (with defaultProps)
    variant: PropTypes.oneOf([VARIANT_MINIMAL, VARIANT_STANDARD]), textAlign: PropTypes.oneOf(['left', 'center', 'right']), size: PropTypes.oneOf(['small', 'medium']), disabled: PropTypes.bool, getValueLength: PropTypes.func, iconLeft: Image.propTypes.source, renderAccessory: PropTypes.func, 
    // Custom propTypes (without defaultProps)
    label: PropTypes.string, helper: PropTypes.string, error: PropTypes.string, characterRestriction: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
            min: PropTypes.number.isRequired,
            max: PropTypes.number.isRequired,
        }),
    ]), 
    // Theme Support
    theme: PropTypes.object });
DRNInputField.defaultProps = {
    // TextInput propTypes
    underlineColorAndroid: 'transparent',
    disableFullscreenUI: true,
    autoCapitalize: 'sentences',
    blurOnSubmit: true,
    editable: true,
    clearButtonMode: 'never',
    // Custom propTypes
    variant: VARIANT_MINIMAL,
    textAlign: I18nManager.isRTL ? 'right' : 'left',
    size: 'medium',
    disabled: false,
    getValueLength: value => value.length,
    iconLeft: null,
    renderAccessory: null,
};
const styles = StyleSheet.create({
    container: {
        backgroundColor: 'transparent',
    },
    input: {
        flex: 1,
        padding: 0,
        margin: 0,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: R.spacing.xs,
    },
    flex: {
        flex: 1,
    },
    helperContainerStyle: {
        flexDirection: 'row',
        marginTop: R.spacing.xs,
    },
    iconLeft: {
        marginRight: R.spacing.xs,
    },
    iconClose: {
        marginLeft: R.spacing.xs,
        width: 16,
        height: 16,
    },
    renderAccessory: {
        marginLeft: R.spacing.xs,
    },
});
const ThemedInputField = withSmartKey(withTheme(DRNInputField));
ThemedInputField.VARIANT_MINIMAL = VARIANT_MINIMAL;
ThemedInputField.VARIANT_STANDARD = VARIANT_STANDARD;
export default ThemedInputField;
